import React, { useMemo, useState } from 'react';

import { css, Global } from '@emotion/react';
import { DatePicker } from '@opendoor/bricks/complex/DatePicker';
import { DropdownSingleSelect, Flex, Input, Text } from '@opendoor/bricks/core';
import { Value } from '@opendoor/bricks/core/Select';
import { Toggle } from '@opendoor/cinderblocks/buy/Toggle';

import { formatCurrency } from 'shared/helpers/formatters';
import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { FundingSource as FundingSourceEnum } from 'buying/components/buyDirectCheckout/commonTypes';
import { useEligibleCloseOfEscrowLocalDates } from 'buying/helpers/useEligibleCloseOfEscrowDates';

interface IPurchaseInfoModuleProps {
  closeDate?: string;
  downPaymentAmount?: number;
  downPaymentPercentage?: number;
  fhaEligibilityDate?: Date;
  fundingSource?: string;
  loanAmount?: number;
  onFinanceChange: (value?: Value) => void;
  onDownPaymentAmountChange: (event: React.ChangeEvent<HTMLInputElement> | undefined) => void;
  onDownPaymentPercentChange: (event: React.ChangeEvent<HTMLInputElement> | undefined) => void;
  onLoanAmountChange: (event: React.ChangeEvent<HTMLInputElement> | undefined) => void;
  onCloseDateChange: (date: Date) => void;
}

const EARLIEST_CLOSE_OF_ESCROW_DAYS = 21;
const MAX_CLOSE_OF_ESCROW_DAYS = 35;

/**
 * Purchase info module
 * storybook-check-ignore
 */
export const PurchaseInfoModule: React.FC<IPurchaseInfoModuleProps> = ({
  closeDate,
  downPaymentAmount,
  downPaymentPercentage,
  fhaEligibilityDate,
  fundingSource,
  loanAmount,
  onFinanceChange,
  onDownPaymentAmountChange,
  onDownPaymentPercentChange,
  onLoanAmountChange,
  onCloseDateChange,
}) => {
  const { isMobile } = useMobileDevice();
  const [downPaymentType, setDownPaymentType] = useState<string>('$');
  const handleToggle = (label: string) => {
    setDownPaymentType(label);
  };

  const fhaDate = useMemo(() => {
    if (fhaEligibilityDate) {
      if (fhaEligibilityDate > new Date()) {
        return fhaEligibilityDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        });
      }
    }
    return undefined;
  }, [fhaEligibilityDate]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- first element is the date range and not used
  const [_dateRange, handleIsOutOfRange] = useEligibleCloseOfEscrowLocalDates(
    EARLIEST_CLOSE_OF_ESCROW_DAYS,
    MAX_CLOSE_OF_ESCROW_DAYS,
  );

  const shouldDisableMortgageInputs =
    fundingSource === FundingSourceEnum.Cash ||
    (fundingSource === FundingSourceEnum.FHA && !!fhaDate) ||
    fundingSource === undefined;

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems={'flex-start'}
        justifyContent="flex-start"
        width="100%"
        mb={8}
      >
        <Text size={isMobile ? '160' : '150'} fontWeight="medium" alignSelf="flex-start">
          Finance info
        </Text>
        <Flex flexDirection="column" justifyContent="space-between" width="100%" mt={8}>
          <DropdownSingleSelect
            aria-labelledby="buy-direct-finance-type-label"
            id="buy-direct-finance-type"
            title="Select the type of loan you have or if you’re buying in cash"
            options={[
              { label: 'Cash', value: FundingSourceEnum.Cash },
              { label: 'Conventional loan', value: FundingSourceEnum.Conventional },
              { label: 'FHA loan', value: FundingSourceEnum.FHA },
              { label: 'VA loan', value: FundingSourceEnum.VA },
              { label: 'USDA loan', value: FundingSourceEnum.USDA },
            ]}
            variant="large"
            isRequired={true}
            placeholder="Loan type or cash"
            selectedValue={[fundingSource as Value]}
            onChange={onFinanceChange}
          />
          {fhaDate ? (
            <>
              <Text
                size="90"
                fontWeight="medium"
                alignSelf="flex-start"
                mt={4}
                ml={[null, null, 2]}
                color="neutrals90"
              >
                Want to use an FHA loan?
              </Text>
              <Text
                size="90"
                fontWeight="regular"
                alignSelf="flex-start"
                mt={4}
                ml={[null, null, 2]}
                color="neutrals90"
              >
                You can use an FHA loan to buy this home {`on ${fhaDate}`}. If you want to buy it
                now, speak with your lender about a different type of loan.
              </Text>
            </>
          ) : null}
          <Flex
            flexDirection={['column', 'column', 'row', 'row']}
            alignItems="baseline"
            my={4}
            width={['100%', '100%', '100%', '100%']}
            justifyContent="space-between"
          >
            <Flex
              flexDirection="column"
              id="down-payment"
              width={['inherit', 'inherit', '55%', null]}
            >
              <Flex flexDirection="row" alignItems="center">
                {(downPaymentType === '$' || downPaymentType === undefined) && (
                  <Input
                    analyticsName="input-down-payment-amount"
                    data-field="down_payment_amount"
                    id="buy-direct-down-payment-amount"
                    type="currency"
                    label="Down payment"
                    invalid={false}
                    disabled={shouldDisableMortgageInputs}
                    required={false}
                    orientation="vertical"
                    placeholder="$0.00"
                    value={(downPaymentAmount ?? 0).toString()}
                    onChange={onDownPaymentAmountChange}
                    mb={[7, null, null]}
                  />
                )}
                {downPaymentType === '%' && (
                  <Input
                    analyticsName="input-down-payment-percentage"
                    data-field="down_payment_percentage"
                    id="buy-direct-down-payment-percentage"
                    type="number"
                    label="Down payment"
                    invalid={false}
                    disabled={shouldDisableMortgageInputs}
                    required={false}
                    orientation="vertical"
                    placeholder="20%"
                    value={(downPaymentPercentage ?? 0).toString()}
                    onChange={onDownPaymentPercentChange}
                    mb={[7, null, null]}
                  />
                )}
                <Flex mt={[2, null, null]}>
                  <Toggle
                    disabled={shouldDisableMortgageInputs}
                    label="Down payment"
                    optionOne="$"
                    optionTwo="%"
                    onChange={handleToggle}
                  />
                </Flex>
              </Flex>
              <Text size="90" color="neutrals90" mb={[4, null, null]} mt={[-4, null, null]}>
                {downPaymentType === '$'
                  ? `${Math.round(downPaymentPercentage ?? 0)}%`
                  : `${formatCurrency(downPaymentAmount ?? 0)}`}
              </Text>
            </Flex>
            <Input
              analyticsName="input-loan-amount"
              data-field="loan_amount"
              id="buy-direct-loan-amount"
              type="currency"
              label="Loan amount"
              disabled={shouldDisableMortgageInputs}
              orientation="vertical"
              placeholder={formatCurrency(loanAmount ?? 0).toString()}
              onChange={onLoanAmountChange}
              mb={[7, null, null]}
            />
          </Flex>
          <DatePicker
            id="buy-direct-close-date"
            label="Chose a close date"
            initialDate={closeDate ? new Date(closeDate) : undefined}
            includeWeekends={false}
            onDateChange={onCloseDateChange}
            isOutOfRange={handleIsOutOfRange}
            customIsOutOfRangeSRMessage="This date is not available"
          />
          <Text size="90" color="neutrals90" mt={4}>
            Most homes need 14 to 30 days to close, so choose a date that works for you in that
            timeframe. If you need to change it, let us know.
          </Text>
        </Flex>
      </Flex>
      <Global
        styles={css`
          label[for='toggle'] {
            font-size: 10px;
          }
        `}
      />
    </>
  );
};

interface IPurchaseInfoProps {
  closeDate?: string;
  downPaymentAmount?: number;
  downPaymentPercentage?: number;
  fundingSource?: FundingSourceEnum;
  listPrice: number;
  loanAmount?: number;
  fhaEligibilityDate?: Date;
  send: (args: string | { type: string; value: string | boolean | number | Value }) => void;
}

function cleanNumberInput(value: string) {
  return parseInt(value.replaceAll(',', '').replaceAll('$', ''));
}

/**
 * Purchase info state machine implementation
 * storybook-check-ignore
 */
export const PurchaseInfo: React.FC<IPurchaseInfoProps> = ({
  closeDate,
  downPaymentAmount,
  downPaymentPercentage,
  fundingSource,
  listPrice,
  loanAmount,
  send,
  fhaEligibilityDate,
}) => {
  const handleOnFinanceChange = (selection?: Value) => {
    if (selection) {
      // When a user selects a VA or USDA loan, we automatically set the down payment to 0 and the loan amount to the list price
      if (selection === FundingSourceEnum.VA || selection === FundingSourceEnum.USDA) {
        send({ type: 'setMortgageDownPaymentAmount', value: 0 });
        send({ type: 'setMortgageDownPaymentPercentage', value: 0 });
        send({ type: 'setLoanAmount', value: listPrice });
      }
      send({ type: 'setFundingSource', value: selection });
    }
  };

  const handleOnDownPaymentAmountChange = (
    event: React.ChangeEvent<HTMLInputElement> | undefined,
  ) => {
    const amount = event?.target.value ? cleanNumberInput(event.target.value) : 0;
    send({ type: 'setMortgageDownPaymentAmount', value: amount });
    send({ type: 'setMortgageDownPaymentPercentage', value: (amount / listPrice) * 100 });
    send({ type: 'setLoanAmount', value: listPrice - amount });
  };

  const handleOnDownPaymentPercentChange = (
    event: React.ChangeEvent<HTMLInputElement> | undefined,
  ) => {
    const percent = event?.target.value ? cleanNumberInput(event.target.value) : 0;
    const amount = (percent / 100) * listPrice;
    send({ type: 'setMortgageDownPaymentPercentage', value: percent });
    send({ type: 'setMortgageDownPaymentAmount', value: (percent / 100) * listPrice });
    send({ type: 'setLoanAmount', value: listPrice - amount });
  };

  const handleOnLoanAmountChange = (event: React.ChangeEvent<HTMLInputElement> | undefined) => {
    const amount = event?.target.value ? cleanNumberInput(event.target.value) : 0;
    send({ type: 'setLoanAmount', value: amount });
  };

  const handleOnCloseDateChange = (date: Date) => {
    send({
      type: 'setCloseDate',
      value: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    });
  };

  return (
    <PurchaseInfoModule
      closeDate={closeDate}
      downPaymentAmount={downPaymentAmount}
      downPaymentPercentage={downPaymentPercentage}
      fundingSource={fundingSource}
      loanAmount={loanAmount}
      onCloseDateChange={handleOnCloseDateChange}
      onDownPaymentAmountChange={handleOnDownPaymentAmountChange}
      onDownPaymentPercentChange={handleOnDownPaymentPercentChange}
      onLoanAmountChange={handleOnLoanAmountChange}
      onFinanceChange={handleOnFinanceChange}
      fhaEligibilityDate={fhaEligibilityDate}
    />
  );
};
